import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import {Button} from '../../../components/ui/Button';
import {Input} from '../../../components/ui/Input';
import apiService from '../../../services/api';

const ParticipantManagementModal = ({ isOpen, onClose, participants, onAddParticipant, onRemoveParticipant, eventId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
 const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [removeConfirmation, setRemoveConfirmation] = useState(null);

  useEffect(() => {
    if (!searchTerm) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const response = await apiService.searchUsers(searchTerm);
        if (response.success) {
          // Filter out already added participants
          const filteredResults = response.data.users?.filter(user => 
            !participants.some(p => p.id === user.id)
          ) || [];
          setSearchResults(filteredResults);
        }
      } catch (error) {
        console.error('Error searching users:', error);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, participants]);

  const handleAddUser = async (user) => {
    try {
      await apiService.addParticipant(eventId, user.id);
      onAddParticipant(user);
      setSearchTerm('');
      setSearchResults([]);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error adding participant:', error);
      alert(error.message || 'Failed to add participant');
    }
  };

  const handleRemoveParticipant = async (participant) => {
    if (removeConfirmation === participant.id) {
      try {
        await apiService.removeParticipant(eventId, participant.id);
        onRemoveParticipant(participant);
        setRemoveConfirmation(null);
      } catch (error) {
        console.error('Error removing participant:', error);
        alert(error.message || 'Failed to remove participant');
      }
    } else {
      setRemoveConfirmation(participant.id);
      // Reset after 5 seconds if not confirmed
      setTimeout(() => {
        if (removeConfirmation === participant.id) {
          setRemoveConfirmation(null);
        }
      }, 5000);
    }
  };

  const handleCancelRemove = () => {
    setRemoveConfirmation(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-lg border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Manage Participants</h2>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          />
        </div>

        <div className="p-6 space-y-6">
          {/* Add Participant Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Add Participant</h3>
            <div className="space-y-3">
              <Input
                label="Search users to add"
                placeholder="Enter name or email..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setSelectedUser(null);
                }}
                autoComplete="off"
              />

              {searchLoading && (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              )}

              {!searchLoading && searchTerm && searchResults.length > 0 && (
                <div className="border border-border rounded-lg max-h-40 overflow-y-auto">
                  {searchResults.map(user => (
                    <div
                      key={user.id}
                      className="p-3 border-b border-border last:border-b-0 hover:bg-muted/50 cursor-pointer flex items-center justify-between"
                      onClick={() => setSelectedUser(user)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-primary-foreground">
                            {user.name?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{user.name}</p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                      {selectedUser?.id === user.id && (
                        <Icon name="Check" size={20} className="text-primary" />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {selectedUser && (
                <div className="p-4 bg-muted/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-primary-foreground">
                          {selectedUser.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{selectedUser.name}</p>
                        <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                      </div>
                    </div>
                    <Button
                      onClick={() => handleAddUser(selectedUser)}
                      className="px-4"
                    >
                      Add
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Current Participants Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Current Participants</h3>
            <div className="space-y-2">
              {participants?.length > 0 ? (
                participants.map(participant => (
                  <div
                    key={participant.id}
                    className="p-3 border border-border rounded-lg flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-primary-foreground">
                          {participant.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{participant.name}</p>
                        <p className="text-sm text-muted-foreground">{participant.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {removeConfirmation === participant.id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-destructive">Remove {participant.name}?</span>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleRemoveParticipant(participant)}
                          >
                            Yes
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCancelRemove}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="destructive"
                          size="sm"
                          iconName="UserMinus"
                          onClick={() => handleRemoveParticipant(participant)}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  No participants added yet
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-border flex justify-end">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ParticipantManagementModal;
